import MediaAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/common/embedded/media/media.entity';
import { OrderDetailDiscountsEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/order-detail-discounts.enum';

// Artículo del documento (línea de la lista) y resumen de totales
export interface DocumentItem {
  // identidad de la línea: material + almacén + unidad. El mismo material puede
  // repetirse en varias líneas, así que _id ya no la identifica por sí solo
  lineId: string;
  _id: string; // materialId; lo usan el precio y la bonificación
  warehouseId?: string; // almacén desde el que se abastece la línea
  warehouseCode?: string;
  warehouseName?: string;
  // disponibilidad por almacén de la oficina; se puebla al distribuir
  warehouseStocks?: WarehouseStockRow[];
  // reparto entre almacenes cuando la clase separa pedidos por almacén: la línea
  // sigue siendo una sola en pantalla y se expande en documentos al generar.
  // Sin esa bandera el reparto se materializa en líneas hermanas y esto queda vacío
  allocations?: WarehouseAllocation[];
  code: string;
  name: string;
  category: string;
  unitLabel: string;
  unitMeasureId: string;
  taxPct: number;
  taxName?: string;
  quantity: number;
  quantityText?: string;
  price: number;
  discount: number; // descuento total de la línea (discountTotal del endpoint)
  discountUnit?: number; // descuento neto por unidad resuelto por el endpoint
  discountType?: OrderDetailDiscountsEnum; // tipo de descuento (AMOUNT/PERCENTAGE/FIXEDAMOUNT)
  isv: number;
  material?: any;
  isCommercialUom?: boolean; // false = unidad no vendible (solo visualizar)
  // decimales de la unidad de la línea; sin configuración se asume que no acepta decimales
  hasAcceptedDecimals?: boolean;
  allowDecimalDistribution?: boolean;
  decimalsAllowed?: number;
  // días de entrega admitidos de la línea, resueltos por el backend
  deliveryWindowDays?: number[];
  manualPrice?: boolean; // precio editado manualmente (canUpdatePrices); no se reprecia
  keepSourcePrice?: boolean; // precio heredado de la cotización origen; no se reprecia
  sourceRef?: DocumentItemSourceRef; // origen (cotización) de esta línea, para encadenar
  priceConditionClasses?: { code: string; value: number }[]; // trazabilidad del precio resuelto
  note?: string; // comentario por línea
  evidences?: MediaAttributes[]; // adjuntos por línea (ya subidos, con link)
  newAttachments?: File[]; // adjuntos nuevos pendientes de subir
  bonusInfo?: DocumentItemBonus; // bono resuelto por el endpoint para esta línea comprada
  bonusIncludedQty?: number; // unidades gratis en la misma línea (INCLUDED_AS_DISCOUNT): chip "+N gratis"
  bonusNotified?: boolean; // ya se avisó de la regalía por esta línea (evita repetir el modal)
  isBonus?: boolean; // la línea es una bonificación (producto gratis)
  bonusParentId?: string; // lineId de la línea comprada que originó el bono
  // la cantidad de esta línea no tiene respaldo de existencia (canMakeAnOrderWithoutStock)
  withoutStock?: boolean;
}

// cantidad asignada a un almacén al distribuir una línea, en la unidad de la línea
export interface WarehouseAllocation {
  warehouseId: string;
  warehouseCode?: string;
  warehouseName?: string;
  quantity: number;
  // el remanente que ningún almacén cubre (solo si el material permite venta sin stock)
  withoutStock?: boolean;
}

// disponibilidad de un material en un almacén, en unidad base
export interface WarehouseStockRow {
  warehouseId: string;
  warehouseCode?: string;
  warehouseName?: string;
  availableStock: number;
  unitOfMeasureId?: string;
}

// la parte sin respaldo se surte del mismo almacén principal, así que necesita
// distinguirse de la línea con existencia de ese almacén
export function buildLineId(
  materialId: string,
  warehouseId: string | null | undefined,
  unitMeasureId: string,
  withoutStock?: boolean
): string {
  const suffix = withoutStock ? ':sin-stock' : '';
  return `${materialId}:${warehouseId ?? ''}:${unitMeasureId}${suffix}`;
}

// Bonificación devuelta por el endpoint de precios
export interface DocumentItemBonus {
  materialId: string;
  unitMeasureId: string;
  quantity: number; // cantidad regalada calculada para la cantidad comprada
  buyQuantity: number; // N de la regla efectiva (por cada N)
  bonusQuantity: number; // G de la regla efectiva (se regala G)
  minimumQuantity?: number; // cantidad mínima que activa la regla
  behaviour: string | null; // SEPARATE_FREE_LINE | INCLUDED_AS_DISCOUNT | null
  sequence?: string; // secuencia ganadora
  sequencePosition?: number | null;
  appliedScale?: DocumentItemBonusScale; // escala usada cuando el rango contiene la cantidad
}

// Escala de bonificación aplicada por el endpoint
export interface DocumentItemBonusScale {
  quantityFrom: number;
  quantityTo: number;
  perQuantity: number;
  bonusQuantity: number;
}

// Material resuelto por sequence-reading/pricing
export interface PricedMaterial {
  materialId: string;
  unitMeasureId: string;
  price: number;
  discount: number; // descuento por unidad
  discountTotal: number; // descuento de línea para la cantidad solicitada
  discountType?: OrderDetailDiscountsEnum;
  conditionClasses?: { code: string; value: number }[];
  bonus?: DocumentItemBonus;
}

export interface PricingResponse {
  materials: PricedMaterial[];
}

// Referencia a la línea de la cotización origen (para sourceTransaction del pedido)
export interface DocumentItemSourceRef {
  serial: string;
  type: any;
  class: any;
  position?: number; // posición de la línea en la cotización origen
}

export interface DocumentSummary {
  itemsCount: number;
  subtotal: number;
  discount: number;
  taxes: number;
  total: number;
}
